import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDebounce } from "use-debounce"
import { useQuery } from "@tanstack/react-query"
import { Box, CloseButton, Dialog, HStack, Input, NativeSelect, Portal, Stack } from "@chakra-ui/react"
import { CompanyInfoFractal, Employee } from "@/types"
import { createCompany, createDeal, getAllFunds, lookupCompanyIds } from "@/api"
import { useSearchedCompanies } from "@/hooks"
import { Button, Text, toaster } from "../ui"
import { EmployeeMatchModal } from "./EmployeeMatchModal"

/** Extract an ID/slug from a pasted URL, or return the raw value if it's not a URL. */
const parseIdFromUrl = (value: string, patterns: RegExp[]): string => {
  const trimmed = value.trim()
  for (const pattern of patterns) {
    const match = trimmed.match(pattern)
    if (match?.[1]) return match[1]
  }
  return trimmed
}

const LINKEDIN_URL_PATTERNS = [
  /linkedin\.com\/company\/([^/?#]+)/i,
]
const CRUNCHBASE_URL_PATTERNS = [
  /crunchbase\.com\/organization\/([^/?#]+)/i,
]
const PITCHBOOK_URL_PATTERNS = [
  /pitchbook\.com\/profiles\/([^/?#]+)/i,
]

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const CreateDealModal = ({ isOpen, onClose }: Props) => {
  const navigate = useNavigate()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [fundId, setFundId] = useState<number | null>(null)
  const [companySearch, setCompanySearch] = useState("")
  const [selectedCompany, setSelectedCompany] = useState<CompanyInfoFractal | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Employee match state
  const [employeeMatches, setEmployeeMatches] = useState<Employee[]>([])
  const [createdDealInfo, setCreatedDealInfo] = useState<{ dealId: number; clientId: number } | null>(null)

  // New company creation state
  const [isCreatingCompany, setIsCreatingCompany] = useState(false)
  const [newCompanyLiId, setNewCompanyLiId] = useState("")
  const [newCompanyLiStrId, setNewCompanyLiStrId] = useState("")
  const [newCompanyPbId, setNewCompanyPbId] = useState("")
  const [newCompanyCbId, setNewCompanyCbId] = useState("")
  const [isCreatingCompanyLoading, setIsCreatingCompanyLoading] = useState(false)
  const [isLookingUpIds, setIsLookingUpIds] = useState(false)

  const [debouncedCompanySearch] = useDebounce(companySearch, 300)

  const { data: companies, isLoading: isSearching } = useSearchedCompanies(
    selectedCompany || isCreatingCompany ? undefined : debouncedCompanySearch.trim() || undefined
  )

  const { data: funds } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["funds"],
    queryFn: getAllFunds,
    staleTime: 1000 * 60 * 30,
  })

  const selectableFunds = useMemo(
    () => funds?.filter((f) => f.name !== "Michigan eLab") ?? [],
    [funds]
  )

  // Default to the latest fund (highest ID)
  useEffect(() => {
    if (fundId === null && selectableFunds.length > 0) {
      const latest = selectableFunds.reduce((a, b) => (b.id > a.id ? b : a))
      setFundId(latest.id)
    }
  }, [selectableFunds, fundId])

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    selectedCompany &&
    fundId !== null &&
    !isSubmitting

  const resetForm = () => {
    setFirstName("")
    setLastName("")
    setEmail("")
    const defaultFund = selectableFunds.length > 0
      ? selectableFunds.reduce((a, b) => (b.id > a.id ? b : a)).id
      : null
    setFundId(defaultFund)
    setCompanySearch("")
    setSelectedCompany(null)
    setEmployeeMatches([])
    setCreatedDealInfo(null)
    setIsCreatingCompany(false)
    setNewCompanyLiId("")
    setNewCompanyLiStrId("")
    setNewCompanyPbId("")
    setNewCompanyCbId("")
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSelectCompany = (company: CompanyInfoFractal) => {
    setSelectedCompany(company)
    setCompanySearch(company.name)
  }

  const handleCompanyInputChange = (value: string) => {
    setCompanySearch(value)
    if (selectedCompany) {
      setSelectedCompany(null)
    }
    if (isCreatingCompany) {
      setIsCreatingCompany(false)
    }
  }

  const handleStartCreateCompany = () => {
    setIsCreatingCompany(true)
    // Auto-suggest LinkedIn String ID from company name
    const slug = companySearch.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    if (slug) setNewCompanyLiStrId(slug)
  }

  const handleCancelCreateCompany = () => {
    setIsCreatingCompany(false)
    setNewCompanyLiId("")
    setNewCompanyLiStrId("")
    setNewCompanyPbId("")
    setNewCompanyCbId("")
    setCompanySearch("")
  }

  const handleLookupIds = async () => {
    if (!companySearch.trim()) return
    setIsLookingUpIds(true)
    try {
      const ids = await lookupCompanyIds(companySearch.trim())
      let found = 0
      if (ids.liStrId) { setNewCompanyLiStrId(ids.liStrId); found++ }
      if (ids.cbId) { setNewCompanyCbId(ids.cbId); found++ }
      if (ids.pbId) { setNewCompanyPbId(ids.pbId); found++ }
      if (found > 0) {
        toaster.success({ title: `Found ${found} ID${found > 1 ? "s" : ""} automatically` })
      } else {
        toaster.create({ title: "No IDs found via search", type: "info" })
      }
    } catch {
      toaster.error({ title: "ID lookup failed" })
    } finally {
      setIsLookingUpIds(false)
    }
  }

  const handleCreateCompany = async () => {
    if (!companySearch.trim()) return

    setIsCreatingCompanyLoading(true)
    try {
      const company = await createCompany({
        name: companySearch.trim(),
        liId: newCompanyLiId ? Number(newCompanyLiId) : null,
        liStrId: newCompanyLiStrId.trim() || null,
        pbId: newCompanyPbId.trim() || null,
        cbId: newCompanyCbId.trim() || null,
      })
      toaster.success({ title: `Company "${company.name}" created` })
      setSelectedCompany(company)
      setIsCreatingCompany(false)
      setNewCompanyLiId("")
      setNewCompanyLiStrId("")
      setNewCompanyPbId("")
      setNewCompanyCbId("")
    } catch {
      toaster.error({ title: "Failed to create company" })
    } finally {
      setIsCreatingCompanyLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || !selectedCompany || fundId === null) return

    setIsSubmitting(true)
    try {
      const { deal, employeeMatches: matches } = await createDeal({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        companyId: selectedCompany.id,
        fundId,
      })
      toaster.success({ title: "Deal created successfully" })

      if (matches.length > 0) {
        // Show employee match modal before navigating
        setEmployeeMatches(matches)
        setCreatedDealInfo({ dealId: deal.id, clientId: deal.clientId })
      } else {
        handleClose()
        navigate(`/core/deal/${deal.id}`)
      }
    } catch {
      toaster.error({ title: "Failed to create deal" })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMatchModalClose = () => {
    const dealId = createdDealInfo?.dealId
    handleClose()
    if (dealId) {
      navigate(`/core/deal/${dealId}`)
    }
  }

  const trimmedSearch = debouncedCompanySearch.trim()
  const hasSearchInput = !selectedCompany && !isCreatingCompany && trimmedSearch.length >= 2
  const showDropdown = hasSearchInput && !isSearching && (companies.length > 0 || trimmedSearch.length > 0)

  // Show employee match modal if we have matches
  if (createdDealInfo && employeeMatches.length > 0) {
    return (
      <EmployeeMatchModal
        isOpen={true}
        onClose={handleMatchModalClose}
        matches={employeeMatches}
        dealId={createdDealInfo.dealId}
        clientId={createdDealInfo.clientId}
        firstName={firstName.trim()}
        lastName={lastName.trim()}
      />
    )
  }

  return (
    <Dialog.Root size="md" open={isOpen} onOpenChange={(e) => !e.open && handleClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="card.bg">
            <Dialog.Header px={6} pt={5} pb={3}>
              <Dialog.Title fontFamily="body" fontWeight="semibold">Create New Deal</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} pb={6}>
              <Stack gap={4}>
                <HStack gap={3}>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>First Name</Text>
                    <Input
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>Last Name</Text>
                    <Input
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Box>
                </HStack>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Email</Text>
                  <Input
                    type="email"
                    placeholder="client@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Fund</Text>
                  <NativeSelect.Root size="md">
                    <NativeSelect.Field
                      value={fundId ?? ""}
                      onChange={(e) => setFundId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Select a fund...</option>
                      {selectableFunds.map((fund) => (
                        <option key={fund.id} value={fund.id}>
                          {fund.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                </Box>

                <Box position="relative">
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Company</Text>
                  {!isCreatingCompany ? (
                    <Input
                      placeholder="Search for a company..."
                      value={companySearch}
                      onChange={(e) => handleCompanyInputChange(e.target.value)}
                    />
                  ) : (
                    <HStack
                      px={3}
                      py={1.5}
                      bg="blue.50"
                      border="1px solid"
                      borderColor="blue.200"
                      borderRadius="md"
                      w="fit-content"
                    >
                      <Text fontSize="sm" fontWeight="medium" color="blue.700">
                        Creating: {companySearch}
                      </Text>
                      <Box
                        cursor="pointer"
                        onClick={handleCancelCreateCompany}
                        _hover={{ opacity: 0.7 }}
                      >
                        <Text fontSize="xs" color="blue.500">x</Text>
                      </Box>
                    </HStack>
                  )}
                  {selectedCompany && (
                    <HStack
                      mt={2}
                      px={3}
                      py={1.5}
                      bg="hover.bg"
                      borderRadius="md"
                      w="fit-content"
                    >
                      <Text fontSize="sm" fontWeight="medium">{selectedCompany.name}</Text>
                      <Box
                        cursor="pointer"
                        onClick={() => { setSelectedCompany(null); setCompanySearch("") }}
                        _hover={{ opacity: 0.7 }}
                      >
                        <Text fontSize="xs" color="text.muted">x</Text>
                      </Box>
                    </HStack>
                  )}
                  {showDropdown && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="card.bg"
                      border="1px solid"
                      borderColor="border.default"
                      borderRadius="md"
                      maxH="200px"
                      overflowY="auto"
                      zIndex={10}
                      boxShadow="md"
                    >
                      {companies.map((company) => (
                        <Box
                          key={company.id}
                          px={4}
                          py={2.5}
                          cursor="pointer"
                          _hover={{ bg: "hover.bg" }}
                          onClick={() => handleSelectCompany(company)}
                        >
                          <Text fontSize="sm">{company.name}</Text>
                        </Box>
                      ))}
                      <Box
                        px={4}
                        py={2.5}
                        cursor="pointer"
                        borderTop="1px solid"
                        borderColor="border.default"
                        _hover={{ bg: "blue.50" }}
                        onClick={handleStartCreateCompany}
                      >
                        <Text fontSize="sm" color="blue.500" fontWeight="medium">
                          + Create "{trimmedSearch}"
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>

                {isCreatingCompany && (
                  <Box
                    p={4}
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="md"
                    bg="blue.50"
                  >
                    <HStack justifyContent="space-between" alignItems="center" mb={3}>
                      <Text fontSize="sm" fontWeight="medium" color="blue.700">
                        External IDs (optional)
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={handleLookupIds}
                        loading={isLookingUpIds}
                        disabled={isLookingUpIds}
                      >
                        Auto-detect IDs
                      </Button>
                    </HStack>
                    <Stack gap={3}>
                      <HStack gap={3}>
                        <Box flex={1}>
                          <Text fontSize="xs" color="text.muted" mb={1}>LinkedIn ID</Text>
                          <Input
                            size="sm"
                            type="number"
                            placeholder="Numeric ID"
                            value={newCompanyLiId}
                            onChange={(e) => setNewCompanyLiId(e.target.value)}
                            bg="white"
                          />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" color="text.muted" mb={1}>LinkedIn String ID</Text>
                          <Input
                            size="sm"
                            placeholder="Slug or LinkedIn URL"
                            value={newCompanyLiStrId}
                            onChange={(e) => setNewCompanyLiStrId(parseIdFromUrl(e.target.value, LINKEDIN_URL_PATTERNS))}
                            bg="white"
                          />
                        </Box>
                      </HStack>
                      <HStack gap={3}>
                        <Box flex={1}>
                          <Text fontSize="xs" color="text.muted" mb={1}>PitchBook ID</Text>
                          <Input
                            size="sm"
                            placeholder="ID or PitchBook URL"
                            value={newCompanyPbId}
                            onChange={(e) => setNewCompanyPbId(parseIdFromUrl(e.target.value, PITCHBOOK_URL_PATTERNS))}
                            bg="white"
                          />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" color="text.muted" mb={1}>CrunchBase ID</Text>
                          <Input
                            size="sm"
                            placeholder="Slug or CrunchBase URL"
                            value={newCompanyCbId}
                            onChange={(e) => setNewCompanyCbId(parseIdFromUrl(e.target.value, CRUNCHBASE_URL_PATTERNS))}
                            bg="white"
                          />
                        </Box>
                      </HStack>
                      <Button
                        size="sm"
                        onClick={handleCreateCompany}
                        loading={isCreatingCompanyLoading}
                        disabled={isCreatingCompanyLoading}
                      >
                        Create Company
                      </Button>
                    </Stack>
                  </Box>
                )}

                <Button
                  mt={2}
                  w="100%"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  Create Deal
                </Button>
              </Stack>
            </Dialog.Body>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
